import {
  ACTIVE_POWER_FULCRUM_FUNCTION_NUMBER,
  ACTIVE_POWER_FUNCTION_DTO,
  ACTIVE_POWER_FUNCTION_MODEL,
  REACTIVE_POWER_FULCRUM_FUNCTION_NUMBER,
  REACTIVE_POWER_FUNCTION_DTO,
  REACTIVE_POWER_FUNCTION_MODEL,
} from '../types/common'
import { GetActivePowerSettingsDto, GetGridAndSystemProtectionDto, GetReactivePowerSettingsDto } from '../types/get-grid-and-system-protection.dto'
import { ActivePowerSettingsModel, GridAndSystemProtectionModel, ReactivePowerSettingsModel } from '../types/grid-and-system-protection.model'
import { ActivePowerSettingsForm, GridAndSystemProtectionForm, ReactivePowerSettingsForm } from '../types/schemas'
import {
  UpdateActivePowerSettingsDto,
  UpdateGridAndSystemProtectionDto,
  UpdateReactivePowerSettingsDto,
} from '../types/update-grid-and-system-protection.dto'
import { valueDependingOnPhaseShift } from '../utils'

//region Get
export const fromGetDtoToModel = (dto: GetGridAndSystemProtectionDto): GridAndSystemProtectionModel => {
  return {
    frequencyMonitoringSettings: dto.frequencyMonitoring
      ? {
          frequencyDropProtection: {
            frequency: dto.frequencyMonitoring.frequencyDropProtection.frequency,
            timeSeconds: dto.frequencyMonitoring.frequencyDropProtection.timeSeconds,
          },
          frequencyGainProtection: {
            frequency: dto.frequencyMonitoring.frequencyGainProtection.frequency,
            timeSeconds: dto.frequencyMonitoring.frequencyGainProtection.timeSeconds,
          },
        }
      : undefined,

    islandDetectionSettings: dto.islandDetection
      ? {
          value: dto.islandDetection.value,
        }
      : undefined,

    lfsmOSettings: dto.lfsmO
      ? {
          thresholdFrequency: dto.lfsmO.thresholdFrequency,
          gradient: dto.lfsmO.gradient,
          returnGradient: dto.lfsmO.returnGradient,
          statics: dto.lfsmO.statics,
          activePowerReference: dto.lfsmO.activePowerReference,
        }
      : undefined,

    lfsmUSettings: dto.lfsmU
      ? {
          thresholdVoltage: dto.lfsmU.thresholdVoltage,
          gradient: dto.lfsmU.gradient,
          returnGradient: dto.lfsmU.returnGradient,
          statics: dto.lfsmU.statics,
          activePowerReference: dto.lfsmU.activePowerReference,
        }
      : undefined,

    mainVoltageLimitsSettings: dto.voltageMonitoring
      ? {
          voltageGainProtection: {
            voltage: dto.voltageMonitoring.voltageGainProtection.voltage,
            timeSeconds: dto.voltageMonitoring.voltageGainProtection.timeSeconds,
            largerVoltage: dto.voltageMonitoring.voltageGainProtection.largerVoltage,
            largerTimeSeconds: dto.voltageMonitoring.voltageGainProtection.largerTimeSeconds,
          },
          voltageDropProtection: {
            voltage: dto.voltageMonitoring.voltageDropProtection.voltage,
            timeSeconds: dto.voltageMonitoring.voltageDropProtection.timeSeconds,
            largerVoltage: dto.voltageMonitoring.voltageDropProtection.largerVoltage,
            largerTimeSeconds: dto.voltageMonitoring.voltageDropProtection.largerTimeSeconds,
          },
        }
      : undefined,

    restartConditionsSettings:
      dto.restartingConditionsTime || dto.restartingConditionsGradient || dto.restartingConditionsVoltage || dto.restartingConditionsFrequency
        ? {
            restartConditionsTimeSettings: dto.restartingConditionsTime,
            restartConditionsGradientSettings: dto.restartingConditionsGradient,
            restartConditionsVoltageSettings: dto.restartingConditionsVoltage,
            restartConditionsFrequencySettings: dto.restartingConditionsFrequency,
          }
        : undefined,

    dynamicGridRegenerationSettings: dto.dynamicGridPower
      ? {
          dynamicReactiveCurrentControl: dto.dynamicGridPower.dynamicReactiveCurrentControl,
          kFactor: {
            positiveSequenceSystem: dto.dynamicGridPower.kFactor.positiveSequenceSystem,
            negativeSequenceSystem: dto.dynamicGridPower.kFactor.negativeSequenceSystem,
          },
        }
      : undefined,

    deadBandThresholdSettings: dto.dynamicGridPower
      ? {
          zeroCurrentMode: {
            lower: dto.dynamicGridPower.zeroCurrentMode.lower,
            upper: dto.dynamicGridPower.zeroCurrentMode.upper,
          },
          reactiveCurrentControl: {
            lower: dto.dynamicGridPower.reactiveCurrentControl.lower,
            upper: dto.dynamicGridPower.reactiveCurrentControl.upper,
          },
        }
      : undefined,

    reactivePowerSettings: dto.reactivePowerSettings
      ? {
          selectedFunction: getReactivePowerSettingsSelectedFunction(dto.reactivePowerSettings.fulcrums),
          threeTau: dto.reactivePowerSettings.threeTau,
          minCosPhi: dto.reactivePowerSettings?.minCosPhi,
          fulcrums: mapReactivePowerFulcrums(dto.reactivePowerSettings.fulcrums),
        }
      : undefined,

    activePowerSettings: dto.activePowerSettings
      ? {
          selectedFunction: getActivePowerSettingsSelectedFunction(dto.activePowerSettings.fulcrums),
          threeTau: dto.activePowerSettings.threeTau,
          fulcrums: mapActivePowerFulcrums(dto.activePowerSettings.fulcrums),
        }
      : undefined,

    errorLogs: dto?.errorLogs,
    areAllClientSettingsSynced: dto?.isEqual,
  }
}

/* Finds the selected (enabled) function for reactive power settings  */
const getReactivePowerSettingsSelectedFunction = (fulcrums: GetReactivePowerSettingsDto['fulcrums']): REACTIVE_POWER_FUNCTION_MODEL => {
  return REACTIVE_POWER_FUNCTION_MODEL[
    Object.keys(
      fulcrums.find(f => {
        const obj = { ...Object.values(f)[0] }
        return obj?.enabled
      }) ?? {}
    )[0] as keyof typeof REACTIVE_POWER_FUNCTION_MODEL
  ]
}

/* Finds the selected (enabled) function for active power settings  */
const getActivePowerSettingsSelectedFunction = (fulcrums: GetActivePowerSettingsDto['fulcrums']): ACTIVE_POWER_FUNCTION_MODEL => {
  return ACTIVE_POWER_FUNCTION_MODEL[
    Object.keys(
      fulcrums.find(f => {
        const obj = { ...Object.values(f)[0] }
        return obj?.enabled
      }) ?? {}
    )[0] as keyof typeof ACTIVE_POWER_FUNCTION_MODEL
  ]
}

const mapReactivePowerFulcrums = (dto: GetReactivePowerSettingsDto['fulcrums']): ReactivePowerSettingsModel['fulcrums'] => {
  return dto.map(f => {
    if (REACTIVE_POWER_FUNCTION_DTO.fixCosPhi in f) {
      const v = f[REACTIVE_POWER_FUNCTION_DTO.fixCosPhi]
      return {
        [REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi]: {
          functionNumber: v.functionNumber,
          value: { value: v.value, min: v.min, max: v.max, changed: v.changed },
        },
      }
    }
    if (REACTIVE_POWER_FUNCTION_DTO.fixedQ in f) {
      const v = f[REACTIVE_POWER_FUNCTION_DTO.fixedQ]
      return {
        [REACTIVE_POWER_FUNCTION_MODEL.fixedQ]: {
          functionNumber: v.functionNumber,
          value: { value: v.value, min: v.min, max: v.max, changed: v.changed },
        },
      }
    }
    if (REACTIVE_POWER_FUNCTION_DTO.cosPhi in f) {
      return { [REACTIVE_POWER_FUNCTION_MODEL.cosPhi]: f[REACTIVE_POWER_FUNCTION_DTO.cosPhi] }
    }
    if (REACTIVE_POWER_FUNCTION_DTO.qU in f) {
      return { [REACTIVE_POWER_FUNCTION_MODEL.qU]: f[REACTIVE_POWER_FUNCTION_DTO.qU] }
    }
    return f as unknown as ReactivePowerSettingsModel['fulcrums'][number]
  })
}

const mapActivePowerFulcrums = (dto: GetActivePowerSettingsDto['fulcrums']): ActivePowerSettingsModel['fulcrums'] =>
  dto.map(f => {
    if (ACTIVE_POWER_FUNCTION_DTO.typeA in f) {
      return { [ACTIVE_POWER_FUNCTION_MODEL.typeA]: f[ACTIVE_POWER_FUNCTION_DTO.typeA] }
    }
    if (ACTIVE_POWER_FUNCTION_DTO.typeB in f) {
      return { [ACTIVE_POWER_FUNCTION_MODEL.typeB]: f[ACTIVE_POWER_FUNCTION_DTO.typeB] }
    }
    return f as unknown as ActivePowerSettingsModel['fulcrums'][number]
  })

//region Update
export const fromFormToUpdateDto = (
  form: Partial<GridAndSystemProtectionForm>
): UpdateGridAndSystemProtectionDto[keyof UpdateGridAndSystemProtectionDto] | null => {
  let dto: UpdateGridAndSystemProtectionDto[keyof UpdateGridAndSystemProtectionDto] | null = null

  if (form.frequencyMonitoringSettings) {
    dto = {
      frequencyGainProtection: {
        frequency: form.frequencyMonitoringSettings.frequencyGainProtection.frequency,
        timeSeconds: form.frequencyMonitoringSettings.frequencyGainProtection.timeSeconds,
      },
      frequencyDropProtection: {
        frequency: form.frequencyMonitoringSettings.frequencyDropProtection.frequency,
        timeSeconds: form.frequencyMonitoringSettings.frequencyDropProtection.timeSeconds,
      },
    }
  }

  if (form.lfsmOSettings) {
    dto = {
      thresholdFrequency: form.lfsmOSettings.thresholdFrequency,
      statics: form.lfsmOSettings.statics,
      gradient: form.lfsmOSettings.gradient,
      returnGradient: form.lfsmOSettings.returnGradient,
      activePowerReference: form.lfsmOSettings.activePowerReference,
    }
  }

  if (form.lfsmUSettings) {
    dto = {
      thresholdVoltage: form.lfsmUSettings.thresholdVoltage,
      statics: form.lfsmUSettings.statics,
      gradient: form.lfsmUSettings.gradient,
      returnGradient: form.lfsmUSettings.returnGradient,
      activePowerReference: form.lfsmUSettings.activePowerReference,
    }
  }

  if (form.mainVoltageLimitsSettings) {
    dto = {
      voltageGainProtection: {
        voltage: form.mainVoltageLimitsSettings.voltageGainProtection.voltage,
        timeSeconds: form.mainVoltageLimitsSettings.voltageGainProtection.timeSeconds,
        largerVoltage: form.mainVoltageLimitsSettings.voltageGainProtection.largerVoltage,
        largerTimeSeconds: form.mainVoltageLimitsSettings.voltageGainProtection.largerTimeSeconds,
      },
      voltageDropProtection: {
        voltage: form.mainVoltageLimitsSettings.voltageDropProtection.voltage,
        timeSeconds: form.mainVoltageLimitsSettings.voltageDropProtection.timeSeconds,
        largerVoltage: form.mainVoltageLimitsSettings.voltageDropProtection.largerVoltage,
        largerTimeSeconds: form.mainVoltageLimitsSettings.voltageDropProtection.largerTimeSeconds,
      },
    }
  }

  if (form?.restartConditionsVoltageSettings) {
    dto = {
      lowerVoltageThresholdFault: form.restartConditionsVoltageSettings.lowerVoltageThresholdFault,
      lowerVoltageThresholdStandard: form.restartConditionsVoltageSettings.lowerVoltageThresholdStandard,
      upperVoltageThresholdFault: form.restartConditionsVoltageSettings.upperVoltageThresholdFault,
      upperVoltageThresholdStandard: form.restartConditionsVoltageSettings.upperVoltageThresholdStandard,
    }
  }

  if (form?.restartConditionsFrequencySettings) {
    dto = {
      lowerFrequencyThresholdFault: form.restartConditionsFrequencySettings.lowerFrequencyThresholdFault,
      lowerFrequencyThresholdStandard: form.restartConditionsFrequencySettings.lowerFrequencyThresholdStandard,
      upperFrequencyThresholdFault: form.restartConditionsFrequencySettings.upperFrequencyThresholdFault,
      upperFrequencyThresholdStandard: form.restartConditionsFrequencySettings.upperFrequencyThresholdStandard,
    }
  }

  if (form?.restartConditionsGradientSettings) {
    dto = {
      gradientFault: form.restartConditionsGradientSettings.gradientFault,
      gradientStandard: form.restartConditionsGradientSettings.gradientStandard,
    }
  }

  if (form?.restartConditionsTimeSettings) {
    dto = {
      delayFault: form.restartConditionsTimeSettings.delayFault,
      delayStandard: form.restartConditionsTimeSettings.delayStandard,
    }
  }

  if (form.dynamicGridRegenerationSettings && form.deadBandThresholdSettings) {
    dto = {
      dynamicReactiveCurrentControl: form.dynamicGridRegenerationSettings.dynamicReactiveCurrentControl,
      kFactor: {
        positiveSequenceSystem: form.dynamicGridRegenerationSettings.kFactor.positiveSequenceSystem,
        negativeSequenceSystem: form.dynamicGridRegenerationSettings.kFactor.negativeSequenceSystem,
      },
      zeroCurrentMode: {
        lower: form.deadBandThresholdSettings.zeroCurrentMode.lower,
        upper: form.deadBandThresholdSettings.zeroCurrentMode.upper,
      },
      reactiveCurrentControl: {
        lower: form.deadBandThresholdSettings.reactiveCurrentControl.lower,
        upper: form.deadBandThresholdSettings.reactiveCurrentControl.upper,
      },
    }
  }

  if (form.reactivePowerSettings?.threeTau && form.reactivePowerSettings?.fulcrums && form.reactivePowerSettings?.selectedFunction) {
    dto = {
      threeTau: form.reactivePowerSettings.threeTau,
      minCosPhi: form.reactivePowerSettings.minCosPhi,
      fulcrums: mapReactivePowerFulcrumsToUpdateDto(form.reactivePowerSettings.fulcrums, form.reactivePowerSettings.selectedFunction),
    }
  }

  if (form.activePowerSettings) {
    dto = {
      threeTau: form.activePowerSettings.threeTau,
      fulcrums: mapActivePowerFulcrumsToUpdateDto(form.activePowerSettings.fulcrums, form.activePowerSettings.selectedFunction),
    }
  }

  return dto
}

const mapReactivePowerFulcrumsToUpdateDto = (
  fulcrums: ReactivePowerSettingsForm['fulcrums'],
  selectedFunction: ReactivePowerSettingsForm['selectedFunction']
): UpdateReactivePowerSettingsDto['fulcrums'] | undefined => {
  const areFulcrumsEmpty =
    fulcrums[REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi]?.length === 0 &&
    fulcrums[REACTIVE_POWER_FUNCTION_MODEL.cosPhi]?.length === 0 &&
    fulcrums[REACTIVE_POWER_FUNCTION_MODEL.qU]?.length === 0 &&
    fulcrums[REACTIVE_POWER_FUNCTION_MODEL.fixedQ]?.length === 0

  if (areFulcrumsEmpty) return undefined
  return [
    {
      [REACTIVE_POWER_FUNCTION_DTO.fixCosPhi]: (() => {
        const fixedCosPhiFulcrum = fulcrums[REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi]?.[0]

        if (!fixedCosPhiFulcrum) return undefined
        return {
          functionNumber: REACTIVE_POWER_FULCRUM_FUNCTION_NUMBER.fixCosPhi,
          values: valueDependingOnPhaseShift(fixedCosPhiFulcrum[1], fixedCosPhiFulcrum[2]),
          enabled: selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi,
        }
      })(),
    },
    {
      [REACTIVE_POWER_FUNCTION_DTO.cosPhi]: {
        functionNumber: REACTIVE_POWER_FULCRUM_FUNCTION_NUMBER.cosPhi,
        values: fulcrums[REACTIVE_POWER_FUNCTION_MODEL.cosPhi]?.map(([a, b, phaseShift]) => [a, valueDependingOnPhaseShift(b, phaseShift)]),
        enabled: selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.cosPhi,
      },
    },
    {
      [REACTIVE_POWER_FUNCTION_DTO.qU]: {
        functionNumber: REACTIVE_POWER_FULCRUM_FUNCTION_NUMBER.qU,
        values: fulcrums[REACTIVE_POWER_FUNCTION_MODEL.qU]?.map(([a, b, phaseShift]) => [a, valueDependingOnPhaseShift(b, phaseShift)]),
        enabled: selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.qU,
      },
    },
    {
      [REACTIVE_POWER_FUNCTION_DTO.fixedQ]: (() => {
        const fixedQFulcrum = fulcrums[REACTIVE_POWER_FUNCTION_MODEL.fixedQ]?.[0]

        if (!fixedQFulcrum) return undefined
        return {
          functionNumber: REACTIVE_POWER_FULCRUM_FUNCTION_NUMBER.fixedQ,
          values: valueDependingOnPhaseShift(fixedQFulcrum[1], fixedQFulcrum[2]),
          enabled: selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.fixedQ,
        }
      })(),
    },
  ]
}

const mapActivePowerFulcrumsToUpdateDto = (
  fulcrums: ActivePowerSettingsForm['fulcrums'],
  selectedFunction: ActivePowerSettingsForm['selectedFunction']
): UpdateActivePowerSettingsDto['fulcrums'] | undefined => {
  const areFulcrumsEmpty = fulcrums[ACTIVE_POWER_FUNCTION_MODEL.typeA].length === 0 && fulcrums[ACTIVE_POWER_FUNCTION_MODEL.typeB].length === 0

  if (areFulcrumsEmpty) return undefined

  return [
    {
      [ACTIVE_POWER_FUNCTION_DTO.typeA]: {
        functionNumber: ACTIVE_POWER_FULCRUM_FUNCTION_NUMBER.typeA,
        values: fulcrums[ACTIVE_POWER_FUNCTION_MODEL.typeA],
        enabled: selectedFunction === ACTIVE_POWER_FUNCTION_MODEL.typeA,
      },
    },
    {
      [ACTIVE_POWER_FUNCTION_DTO.typeB]: {
        functionNumber: ACTIVE_POWER_FULCRUM_FUNCTION_NUMBER.typeB,
        values: fulcrums[ACTIVE_POWER_FUNCTION_MODEL.typeB],
        enabled: selectedFunction === ACTIVE_POWER_FUNCTION_MODEL.typeB,
      },
    },
  ]
}
